import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useLocation } from "wouter";
import { absolutePath } from "@/lib/routes";
import {
  ASK_ADMIN_DBT_REPOSITORY,
  ASK_ADMIN_DESTINATION,
  DBT_SETTINGS_PATH,
  OPEN_DBT_SETTINGS_LABEL,
  OPEN_WAREHOUSE_SETTINGS_LABEL,
  WAREHOUSE_SETTINGS_PATH,
} from "@/lib/transformations/dbtSettings";
import type { LandingBar, LandingBarAction } from "@/lib/transformations/landingStatus";

/**
 * At most one bar on the Transformations landing, and only when something needs action (Session
 * 11, P5; ux-s11-transformations.md §2.5, the nine rows in their priority). `landingBar()` picks
 * the bar; this renders it with its actions. No bar for a healthy state, a run in flight, a skip
 * the customer cannot fix, or warned tests.
 *
 * Viewer-safe: every action navigates (View run, the destination settings) or switches the view
 * (Show models, Show tests); nothing here writes. The settings action is rendered only for a role
 * that may read the destination binding (`instance.binding.read`): "Open dbt repository
 * settings" (the dbt repository tab) for a repository fix, "Open Destination" (the
 * Warehouse tab) for a warehouse fix (round 6, T2).
 *
 * The wrapper carries `data-bar` (the UX §2.5 condition) and the stable id "Run transformations
 * now" points its `aria-describedby` at when nothing can run.
 */

export const TRANSFORMATION_ACTION_BAR_ID = "transformation-action-bar";

export interface TransformationActionBarsProps {
  bar: LandingBar | null;
  canOpenDestination: boolean;
  /** "Show models" / "Show tests": the landing sets `?view=` and `?result=failed`. */
  onShow(view: "models" | "tests"): void;
}

const LABELS: Record<LandingBarAction, string> = {
  "view-run": "View run",
  "open-destination": OPEN_DBT_SETTINGS_LABEL,
  "show-models": "Show models",
  "show-tests": "Show tests",
};

/**
 * Round 6 (T2): the bars whose fix is on the Destination screen's Warehouse tab (the warehouse
 * sign-in, a Databricks destination) keep "Open Destination" and open that tab; every other
 * "open-destination" bar is about the repository and opens the dbt repository tab.
 */
function isWarehouseFix(bar: LandingBar): boolean {
  return bar.warehouseFix === true;
}

export function TransformationActionBars({
  bar,
  canOpenDestination,
  onShow,
}: TransformationActionBarsProps) {
  const [, navigate] = useLocation();
  if (!bar) return null;

  const run = (action: LandingBarAction): void => {
    switch (action) {
      case "view-run":
        if (bar.runId) navigate(absolutePath(`/runs/${bar.runId}`));
        return;
      case "open-destination":
        // Straight to the tab the fix is on (Session 10 P5; round 6, T2).
        navigate(absolutePath(isWarehouseFix(bar) ? WAREHOUSE_SETTINGS_PATH : DBT_SETTINGS_PATH));
        return;
      case "show-models":
        onShow("models");
        return;
      case "show-tests":
        onShow("tests");
        return;
    }
  };

  const actions = bar.actions.filter(
    (action) =>
      (action !== "open-destination" || canOpenDestination) &&
      (action !== "view-run" || bar.runId !== null),
  );
  // #1106: a reader who cannot open the Destination tabs reads who acts in place of the button.
  const askLine =
    bar.actions.includes("open-destination") && !canOpenDestination
      ? isWarehouseFix(bar)
        ? ASK_ADMIN_DESTINATION
        : ASK_ADMIN_DBT_REPOSITORY
      : null;

  return (
    <div
      id={TRANSFORMATION_ACTION_BAR_ID}
      data-testid="transformation-action-bar"
      data-bar={bar.kind}
    >
      <SkyMessageBar
        intent={bar.intent}
        layout="multiline"
        message={askLine ? `${bar.message} ${askLine}` : bar.message}
        actions={
          actions.length > 0 ? (
            <span className="flex flex-wrap gap-2">
              {actions.map((action, index) => (
                <SkyButton
                  key={action}
                  appearance={index === 0 ? "secondary" : "subtle"}
                  size="small"
                  onClick={() => run(action)}
                  testId={`transformation-action-bar-${action}`}
                >
                  {action === "open-destination" && isWarehouseFix(bar)
                    ? OPEN_WAREHOUSE_SETTINGS_LABEL
                    : LABELS[action]}
                </SkyButton>
              ))}
            </span>
          ) : undefined
        }
      />
    </div>
  );
}
